import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  BadgeIndianRupee,
  CalendarClock,
  FileText,
  HandCoins,
  ReceiptText,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { EmptyState, Loading, PageHeader, inr } from "../../components/shared";

const PERMISSION = (user, module, action = "view") =>
  user?.role === "super_admin" || Boolean(user?.permissions?.[module]?.[action]);

const STATUS_STYLE = {
  PAID: "bg-emerald-100 text-emerald-800",
  PARTIALLY_PAID: "bg-amber-100 text-amber-800",
  UNPAID: "bg-rose-100 text-rose-800",
  active: "bg-emerald-100 text-emerald-800",
  trial: "bg-amber-100 text-amber-800",
  expired: "bg-rose-100 text-rose-800",
  waived: "bg-blue-100 text-blue-800",
};

export default function AdminCommercial() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [records, setRecords] = useState([]);
  const [waivers, setWaivers] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [invoiceFilter, setInvoiceFilter] = useState("billed");
  const [renewalType, setRenewalType] = useState("all");
  const [renewalWindow, setRenewalWindow] = useState("30");
  const [renewalStart, setRenewalStart] = useState("");
  const [renewalEnd, setRenewalEnd] = useState("");
  const [receiptInvoiceId, setReceiptInvoiceId] = useState("");
  const [tab, setTab] = useState("overview");

  const canCollections = PERMISSION(user, "collections");
  const canReports = PERMISSION(user, "commercial_reports");
  const canRenewals = PERMISSION(user, "renewals");
  const canWaivers = PERMISSION(user, "waivers");
  const canCrm = PERMISSION(user, "crm_leads");
  const canQuotes = PERMISSION(user, "commercial_quotes");

  const loadDashboard = async () => {
    try {
      const response = await api.get("/admin/commercial/dashboard");
      setDashboard(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load Commercial Control");
      setDashboard({});
    }
  };

  const loadCollections = async (filter = invoiceFilter) => {
    if (!canCollections) return;
    try {
      const [invoiceResponse, receiptResponse, recordResponse] = await Promise.all([
        api.get("/admin/commercial/invoices", { params: { filter } }),
        api.get("/admin/commercial/receipts"),
        api.get("/admin/commercial/records"),
      ]);
      setInvoices(invoiceResponse.data);
      setReceipts(receiptResponse.data);
      setRecords(recordResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load finance traceability");
    }
  };

  const loadRenewals = async () => {
    if (!canRenewals) return;
    try {
      const response = await api.get("/admin/commercial/renewals", {
        params: {
          type: renewalType,
          window: renewalWindow,
          start_date: renewalStart,
          end_date: renewalEnd,
        },
      });
      setRenewals(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load expiry records");
    }
  };

  const loadReports = async () => {
    if (!canReports) return;
    try {
      const response = await api.get("/admin/commercial/reconciliation");
      setReconciliation(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load reconciliation");
    }
  };

  const loadWaivers = async () => {
    if (!canWaivers) return;
    try {
      const response = await api.get("/admin/commercial/waivers");
      setWaivers(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load waiver records");
    }
  };

  useEffect(() => {
    const requestedFilter = searchParams.get("filter");
    const requestedTab = searchParams.get("tab");
    const requestedType = searchParams.get("type");
    const filter = ["billed", "paid", "outstanding", "overdue"].includes(requestedFilter)
      ? requestedFilter
      : "billed";
    if (requestedFilter) setInvoiceFilter(filter);
    if (requestedTab === "renewals") setTab("renewals");
    if (requestedType && ["society", "vendor", "amc"].includes(requestedType)) {
      setRenewalType(requestedType);
    }
    loadDashboard();
    loadCollections(filter);
    loadRenewals();
    loadReports();
    loadWaivers();
  }, []);

  useEffect(() => {
    loadRenewals();
  }, [renewalType, renewalWindow, renewalStart, renewalEnd]);

  const showInvoices = async (filter) => {
    setInvoiceFilter(filter);
    setTab("invoices");
    await loadCollections(filter);
  };

  const refresh = async () => {
    await Promise.all([
      loadDashboard(),
      loadCollections(),
      loadRenewals(),
      loadReports(),
      loadWaivers(),
    ]);
    toast.success("Commercial data refreshed");
  };

  const currentInvoices = useMemo(() => invoices || [], [invoices]);
  const visibleReceipts = useMemo(
    () => receipts.filter((item) => !receiptInvoiceId || item.invoice_id === receiptInvoiceId),
    [receiptInvoiceId, receipts],
  );

  if (!dashboard) return <Loading />;

  return (
    <div data-testid="commercial-control-page">
      <PageHeader
        title="Commercial Control"
        subtitle="Finance-backed billing, collections, renewals, and reconciliation."
      >
        {canQuotes && (
          <Link data-testid="commercial-open-quotes-link" to="/admin/commercial-quotes">
            <Button data-testid="commercial-open-quotes-button" type="button" variant="outline">
              <FileText className="h-4 w-4" /> Quotations
            </Button>
          </Link>
        )}
        <Button data-testid="commercial-refresh-button" onClick={refresh} variant="outline">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </PageHeader>

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricButton
          icon={BadgeIndianRupee}
          label="Billed"
          onClick={() => showInvoices("billed")}
          testId="commercial-billed-card"
          tone="blue"
          value={inr(dashboard.billed)}
        />
        <MetricButton
          icon={ReceiptText}
          label="Collected"
          onClick={() => showInvoices("paid")}
          testId="commercial-collected-card"
          tone="green"
          value={inr(dashboard.collected)}
        />
        <MetricButton
          icon={HandCoins}
          label="Outstanding"
          onClick={() => showInvoices("outstanding")}
          testId="commercial-outstanding-card"
          tone="amber"
          value={inr(dashboard.outstanding)}
        />
        <MetricButton
          icon={AlertTriangle}
          label="Overdue"
          onClick={() => showInvoices("overdue")}
          testId="commercial-overdue-card"
          tone="rose"
          value={inr(dashboard.overdue)}
        />
      </section>

      <section className="mt-6 grid gap-4 lg:grid-cols-3">
        <LifecycleBand
          heading="Society subscriptions"
          metrics={dashboard.society}
          onOpen={() => {
            setRenewalType("society");
            setTab("renewals");
          }}
          testId="commercial-society-lifecycle"
        />
        <LifecycleBand
          heading="Vendor subscriptions"
          metrics={dashboard.vendor}
          onOpen={() => {
            setRenewalType("vendor");
            setTab("renewals");
          }}
          testId="commercial-vendor-lifecycle"
        />
        <button
          className="border bg-card p-5 text-left shadow-sm transition-transform hover:-translate-y-0.5"
          data-testid="commercial-amc-lifecycle"
          onClick={() => {
            setRenewalType("amc");
            setTab("renewals");
          }}
        >
          <div className="flex items-center gap-2 font-semibold">
            <CalendarClock className="h-4 w-4 text-primary" />
            AMC renewals
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <TinyMetric label="Expiring in 30 days" value={dashboard.amc?.expiring} />
            <TinyMetric label="Expired" value={dashboard.amc?.expired} />
          </div>
        </button>
      </section>

      <section className="mt-6 border-l-4 border-primary bg-primary/5 px-5 py-4" data-testid="commercial-adjustments-summary">
        <div className="grid gap-4 sm:grid-cols-3">
          <TinyMetric label="Recorded discounts" value={inr(dashboard.discount_value)} />
          <TinyMetric label="Recorded waived value" value={inr(dashboard.waived_value)} />
          <TinyMetric label="Waiver records" value={dashboard.waiver_count} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Discounts and waivers reduce billable value. They are not collections and never create cash,
          receipt, or income entries.
        </p>
      </section>

      <Tabs className="mt-8" onValueChange={setTab} value={tab}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger data-testid="commercial-overview-tab" value="overview">Overview</TabsTrigger>
          {canCollections && (
            <TabsTrigger data-testid="commercial-invoices-tab" value="invoices">Invoices</TabsTrigger>
          )}
          {canCollections && (
            <TabsTrigger data-testid="commercial-receipts-tab" value="receipts">Receipts</TabsTrigger>
          )}
          {canWaivers && (
            <TabsTrigger data-testid="commercial-waivers-tab" value="waivers">Waivers</TabsTrigger>
          )}
          {canRenewals && (
            <TabsTrigger data-testid="commercial-renewals-tab" value="renewals">Renewals</TabsTrigger>
          )}
          {canReports && (
            <TabsTrigger data-testid="commercial-reconciliation-tab" value="reconciliation">
              Reconciliation
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview">
          <RecordsTable
            canCrm={canCrm}
            onShowReceipts={(invoiceId) => {
              setReceiptInvoiceId(invoiceId);
              setTab("receipts");
            }}
            records={records}
            setTab={setTab}
          />
        </TabsContent>
        {canCollections && (
          <TabsContent value="invoices">
            <div className="mb-4 flex flex-wrap gap-2">
              {[
                ["billed", "All billed"],
                ["paid", "With collections"],
                ["outstanding", "Outstanding"],
                ["overdue", "Overdue"],
              ].map(([value, label]) => (
                <Button
                  data-testid={`commercial-invoice-filter-${value}`}
                  key={value}
                  onClick={() => showInvoices(value)}
                  size="sm"
                  variant={invoiceFilter === value ? "default" : "outline"}
                >
                  {label}
                </Button>
              ))}
            </div>
            <InvoiceTable
              invoices={currentInvoices}
              onShowReceipts={(invoiceId) => {
                setReceiptInvoiceId(invoiceId);
                setTab("receipts");
              }}
            />
          </TabsContent>
        )}
        {canCollections && (
          <TabsContent value="receipts">
            <ReceiptTable
              onClear={() => setReceiptInvoiceId("")}
              receiptInvoiceId={receiptInvoiceId}
              receipts={visibleReceipts}
            />
          </TabsContent>
        )}
        {canWaivers && (
          <TabsContent value="waivers">
            <WaiverTable waivers={waivers} />
          </TabsContent>
        )}
        {canRenewals && (
          <TabsContent value="renewals">
            <RenewalTable
              renewalType={renewalType}
              renewalWindow={renewalWindow}
              renewalEnd={renewalEnd}
              renewalStart={renewalStart}
              renewals={renewals}
              setRenewalType={setRenewalType}
              setRenewalWindow={setRenewalWindow}
              setRenewalEnd={setRenewalEnd}
              setRenewalStart={setRenewalStart}
            />
          </TabsContent>
        )}
        {canReports && (
          <TabsContent value="reconciliation">
            <Reconciliation reconciliation={reconciliation} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function MetricButton({ icon: Icon, label, onClick, testId, tone, value }) {
  const toneClass = {
    blue: "border-primary/25 bg-primary/5 text-primary",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  }[tone];
  return (
    <button
      className={`border p-5 text-left shadow-sm transition-transform hover:-translate-y-0.5 ${toneClass}`}
      data-testid={testId}
      onClick={onClick}
    >
      <Icon className="h-5 w-5" />
      <p className="mt-5 font-display text-2xl font-bold text-foreground">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </button>
  );
}

function LifecycleBand({ heading, metrics, onOpen, testId }) {
  return (
    <button
      className="border bg-card p-5 text-left shadow-sm transition-transform hover:-translate-y-0.5"
      data-testid={testId}
      onClick={onOpen}
    >
      <div className="font-semibold">{heading}</div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <TinyMetric label="Active" value={metrics?.active} />
        <TinyMetric label="Trial" value={metrics?.trial} />
        <TinyMetric label="Expiring in 30 days" value={metrics?.expiring} />
        <TinyMetric label="Expired" value={metrics?.expired} />
      </div>
    </button>
  );
}

function TinyMetric({ label, value }) {
  return (
    <div>
      <div className="font-display text-lg font-bold">{value ?? 0}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function RecordsTable({ canCrm, onShowReceipts, records, setTab }) {
  if (!records.length) {
    return <EmptyState icon={FileText} title="No finance-backed commercial records yet" />;
  }
  return (
    <section className="mt-5 overflow-x-auto border bg-card" data-testid="commercial-records-table">
      <table className="min-w-[1120px] w-full text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            <th className="p-3">Party / plan</th>
            <th className="p-3">Standard</th>
            <th className="p-3">Discount</th>
            <th className="p-3">Waiver</th>
            <th className="p-3">Billable</th>
            <th className="p-3">Invoice</th>
            <th className="p-3">Collected</th>
            <th className="p-3">Outstanding</th>
            <th className="p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr className="border-t" data-testid={`commercial-record-${record.invoice_id}`} key={record.invoice_id}>
              <td className="p-3">
                <div className="font-medium">{record.party_name}</div>
                <div className="text-xs text-muted-foreground">
                  {record.source_type} · {record.billing_period}
                </div>
              </td>
              <td className="p-3">{inr(record.standard_value)}</td>
              <td className="p-3">{inr(record.discount_value)}</td>
              <td className="p-3">{inr(record.waiver_value)}</td>
              <td className="p-3 font-semibold">{inr(record.billable_amount)}</td>
              <td className="p-3">
                <div>{record.invoice_number}</div>
                <Status value={record.status} />
              </td>
              <td className="p-3">{inr(record.paid)}</td>
              <td className="p-3">{inr(record.outstanding)}</td>
              <td className="p-3">
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid={`commercial-view-invoice-${record.invoice_id}`}
                    onClick={() => setTab("invoices")}
                    size="sm"
                    variant="outline"
                  >
                    View invoice
                  </Button>
                  {record.receipt_count > 0 && (
                    <Button
                      data-testid={`commercial-view-receipts-${record.invoice_id}`}
                      onClick={() => onShowReceipts(record.invoice_id)}
                      size="sm"
                      variant="outline"
                    >
                      Receipts ({record.receipt_count})
                    </Button>
                  )}
                  <Link data-testid={`commercial-record-receipt-${record.invoice_id}`} to="/admin/company-finance">
                    <Button size="sm" variant="outline">Record receipt</Button>
                  </Link>
                  {canCrm && record.crm && <CrmActions crm={record.crm} invoiceId={record.invoice_id} />}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function CrmActions({ crm, invoiceId }) {
  return (
    <>
      <Link data-testid={`commercial-add-followup-${invoiceId}`} to={`/admin/leads?open=${crm.lead_id}`}>
        <Button size="sm">Add follow-up</Button>
      </Link>
      <Link data-testid={`commercial-followup-history-${invoiceId}`} to={`/admin/leads?open=${crm.lead_id}`}>
        <Button size="sm" variant="outline">History</Button>
      </Link>
      {crm.mobile && (
        <a data-testid={`commercial-call-${invoiceId}`} href={`tel:${crm.mobile}`}>
          <Button size="sm" variant="outline">Call</Button>
        </a>
      )}
      {crm.mobile && (
        <a
          data-testid={`commercial-whatsapp-${invoiceId}`}
          href={`https://wa.me/${String(crm.mobile).replace(/\D/g, "")}`}
          rel="noreferrer"
          target="_blank"
        >
          <Button size="sm" variant="outline">WhatsApp</Button>
        </a>
      )}
      {crm.email && (
        <a data-testid={`commercial-email-${invoiceId}`} href={`mailto:${crm.email}`}>
          <Button size="sm" variant="outline">Email</Button>
        </a>
      )}
    </>
  );
}

function InvoiceTable({ invoices, onShowReceipts }) {
  if (!invoices.length) {
    return <EmptyState icon={FileText} title="No matching Company Finance invoices" />;
  }
  return (
    <section className="overflow-x-auto border bg-card" data-testid="commercial-invoices-table">
      <table className="min-w-[980px] w-full text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            <th className="p-3">Invoice</th><th className="p-3">Party</th><th className="p-3">Due</th>
            <th className="p-3">Billed</th><th className="p-3">Collected</th><th className="p-3">Outstanding</th>
            <th className="p-3">Action</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr className="border-t" data-testid={`commercial-invoice-${invoice.invoice_id}`} key={invoice.invoice_id}>
              <td className="p-3"><div className="font-medium">{invoice.invoice_number}</div><Status value={invoice.status} /></td>
              <td className="p-3">{invoice.party_name}</td>
              <td className="p-3">{invoice.due_date || "—"}</td>
              <td className="p-3">{inr(invoice.total)}</td>
              <td className="p-3">{inr(invoice.paid)}</td>
              <td className="p-3">{inr(invoice.outstanding)}</td>
              <td className="p-3">
                <div className="flex gap-2">
                  <Button
                    data-testid={`commercial-invoice-receipts-${invoice.invoice_id}`}
                    onClick={() => onShowReceipts(invoice.invoice_id)}
                    size="sm"
                    variant="outline"
                  >
                    Receipts
                  </Button>
                  <Link
                    data-testid={`commercial-invoice-record-receipt-${invoice.invoice_id}`}
                    to="/admin/company-finance"
                  >
                    <Button size="sm">Record receipt</Button>
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ReceiptTable({ onClear, receiptInvoiceId, receipts }) {
  if (!receipts.length) {
    return <EmptyState icon={ReceiptText} title="No Company Finance receipts for commercial invoices" />;
  }
  return (
    <section className="overflow-x-auto border bg-card" data-testid="commercial-receipts-table">
      {receiptInvoiceId && (
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-xs text-muted-foreground">Invoice {receiptInvoiceId}</span>
          <Button data-testid="commercial-clear-receipt-filter" onClick={onClear} size="sm" variant="outline">
            Show all receipts
          </Button>
        </div>
      )}
      <table className="min-w-[760px] w-full text-sm">
        <thead className="bg-muted/60 text-left"><tr><th className="p-3">Receipt</th><th className="p-3">Date</th><th className="p-3">Invoice</th><th className="p-3">Mode</th><th className="p-3">Amount</th><th className="p-3">Reference</th></tr></thead>
        <tbody>{receipts.map((receipt) => <tr className="border-t" data-testid={`commercial-receipt-${receipt.receipt_id}`} key={receipt.receipt_id}><td className="p-3 font-medium">{receipt.receipt_number}</td><td className="p-3">{receipt.date}</td><td className="p-3 font-mono text-xs">{receipt.invoice_id}</td><td className="p-3">{receipt.mode}</td><td className="p-3">{inr(receipt.amount)}</td><td className="p-3">{receipt.reference || "—"}</td></tr>)}</tbody>
      </table>
    </section>
  );
}

function WaiverTable({ waivers }) {
  if (!waivers.length) {
    return <EmptyState icon={HandCoins} title="No waiver records" />;
  }
  return (
    <section className="overflow-x-auto border bg-card" data-testid="commercial-waivers-table">
      <table className="min-w-[820px] w-full text-sm">
        <thead className="bg-muted/60 text-left"><tr><th className="p-3">Party</th><th className="p-3">Type</th><th className="p-3">Period</th><th className="p-3">Standard</th><th className="p-3">Discount</th><th className="p-3">Waived</th><th className="p-3">Billable</th></tr></thead>
        <tbody>{waivers.map((waiver) => <tr className="border-t" data-testid={`commercial-waiver-${waiver.waiver_id}`} key={waiver.waiver_id}><td className="p-3 font-medium">{waiver.party_name}</td><td className="p-3 capitalize">{waiver.target_type}</td><td className="p-3">{waiver.period}</td><td className="p-3">{inr(waiver.standard_value)}</td><td className="p-3">{inr(waiver.discount_value)}</td><td className="p-3">{inr(waiver.waiver_value)}</td><td className="p-3">{inr(waiver.billable_amount)}</td></tr>)}</tbody>
      </table>
    </section>
  );
}

function RenewalTable({
  renewalEnd,
  renewalStart,
  renewalType,
  renewalWindow,
  renewals,
  setRenewalEnd,
  setRenewalStart,
  setRenewalType,
  setRenewalWindow,
}) {
  return (
    <section data-testid="commercial-renewals-panel">
      <div className="mb-4 flex flex-wrap gap-3">
        <select data-testid="commercial-renewal-type-filter" onChange={(event) => setRenewalType(event.target.value)} value={renewalType}><option value="all">All records</option><option value="society">Society subscriptions</option><option value="vendor">Vendor subscriptions</option><option value="amc">AMCs</option></select>
        <select data-testid="commercial-renewal-window-filter" onChange={(event) => setRenewalWindow(event.target.value)} value={renewalWindow}><option value="expired">Expired</option><option value="today">Today</option><option value="7">Next 7 days</option><option value="15">Next 15 days</option><option value="30">Next 30 days</option><option value="60">Next 60 days</option><option value="90">Next 90 days</option><option value="custom">Custom range</option></select>
      </div>
      {renewalWindow === "custom" && <div className="mb-4 flex flex-wrap gap-3"><input data-testid="commercial-renewal-start-date" onChange={(event) => setRenewalStart(event.target.value)} type="date" value={renewalStart} /><input data-testid="commercial-renewal-end-date" onChange={(event) => setRenewalEnd(event.target.value)} type="date" value={renewalEnd} /></div>}
      {!renewals.length ? <EmptyState icon={CalendarClock} title="No matching expiry records" /> : <section className="overflow-x-auto border bg-card"><table className="min-w-[720px] w-full text-sm"><thead className="bg-muted/60 text-left"><tr><th className="p-3">Record</th><th className="p-3">Type</th><th className="p-3">Plan / vendor</th><th className="p-3">Expiry</th><th className="p-3">Status</th></tr></thead><tbody>{renewals.map((item) => <tr className="border-t" data-testid={`commercial-renewal-${item.record_id}`} key={`${item.record_type}-${item.record_id}`}><td className="p-3 font-medium">{item.name}</td><td className="p-3 capitalize">{item.record_type}</td><td className="p-3">{item.plan}</td><td className="p-3">{item.date}</td><td className="p-3"><Status value={item.status} /></td></tr>)}</tbody></table></section>}
    </section>
  );
}

function Reconciliation({ reconciliation }) {
  if (!reconciliation) return <Loading />;
  const rows = ["billed", "collected", "outstanding"];
  return (
    <section data-testid="commercial-reconciliation-panel">
      <div className="overflow-x-auto border bg-card"><table className="min-w-[700px] w-full text-sm"><thead className="bg-muted/60 text-left"><tr><th className="p-3">Measure</th><th className="p-3">Commercial</th><th className="p-3">Company Finance</th><th className="p-3">Difference</th></tr></thead><tbody>{rows.map((row) => <tr className="border-t" data-testid={`commercial-reconciliation-${row}`} key={row}><td className="p-3 capitalize font-medium">{row}</td><td className="p-3">{inr(reconciliation.commercial?.[row])}</td><td className="p-3">{inr(reconciliation.company_finance?.[row])}</td><td className="p-3">{inr(reconciliation.difference?.[row])}</td></tr>)}</tbody></table></div>
      <section className="mt-5 overflow-x-auto border bg-card"><table className="min-w-[620px] w-full text-sm"><thead className="bg-muted/60 text-left"><tr><th className="p-3">Source</th><th className="p-3">Billed</th><th className="p-3">Collected</th><th className="p-3">Outstanding</th></tr></thead><tbody>{reconciliation.by_source?.map((item) => <tr className="border-t" data-testid={`commercial-reconciliation-source-${item.source}`} key={item.source}><td className="p-3 capitalize">{item.source.replace("_", " ")}</td><td className="p-3">{inr(item.billed)}</td><td className="p-3">{inr(item.collected)}</td><td className="p-3">{inr(item.outstanding)}</td></tr>)}</tbody></table></section>
      <p className="mt-4 text-xs text-muted-foreground">Differences are displayed for review only. This workspace never posts a reconciliation adjustment.</p>
    </section>
  );
}

function Status({ value }) {
  return <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[value] || "bg-muted text-muted-foreground"}`}>{String(value || "—").replaceAll("_", " ")}</span>;
}